/**
 * Superadmin — Tenant Detail Page
 * View and edit a single tenant's settings, billing, invoices
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL || 'https://api.shunrindo.com/api/v1';

function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
}

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  trialing: 'bg-blue-100 text-blue-800',
  past_due: 'bg-yellow-100 text-yellow-800',
  suspended: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlanId, setSelectedPlanId] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/superadmin/tenants/${id}`, { headers: getAuthHeaders() }),
      axios.get(`${API}/superadmin/plans`, { headers: getAuthHeaders() })
    ]).then(([tenantRes, plansRes]) => {
      setData(tenantRes.data);
      setPlans(plansRes.data.plans);
      const t = tenantRes.data.tenant;
      setForm({
        subscriptionStatus: t.subscriptionStatus,
        planId: t.planId,
        isActive: t.isActive,
        isSuspended: t.isSuspended,
        contactEmail: t.contactEmail
      });
      setSelectedPlanId(t.planId || '');
    }).catch(err => {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load tenant' });
    }).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.patch(
        `${API}/superadmin/tenants/${id}`,
        {
          subscriptionStatus: form.subscriptionStatus,
          planId: form.planId ? Number(form.planId) : undefined,
          isActive: form.isActive,
          isSuspended: form.isSuspended,
          contactEmail: form.contactEmail
        },
        { headers: getAuthHeaders() }
      );
      setMessage({ type: 'success', text: 'Tenant updated successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update tenant' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendPaymentLink = async () => {
    if (!selectedPlanId) return alert('Please select a plan first');
    setSendingLink(true);
    setMessage(null);
    try {
      const res = await axios.post(
        `${API}/superadmin/tenants/${id}/send-payment-link`,
        { planId: Number(selectedPlanId), billingCycle },
        { headers: getAuthHeaders() }
      );
      setMessage({
        type: 'success',
        text: `Payment link created! Invoice: ${res.data.invoiceNumber} — €${res.data.amount}`
      });
      // Open checkout in new tab
      if (res.data.checkoutUrl) {
        window.open(res.data.checkoutUrl, '_blank');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create payment link' });
    } finally {
      setSendingLink(false);
    }
  };

  const handleImpersonate = async () => {
    if (!window.confirm(`Switch to viewing as "${data.tenant.businessName}"?`)) return;
    try {
      const res = await axios.post(
        `${API}/superadmin/impersonate/${id}`,
        {},
        { headers: getAuthHeaders() }
      );
      localStorage.setItem('superadminToken', localStorage.getItem('accessToken'));
      localStorage.setItem('superadminRefreshToken', localStorage.getItem('refreshToken') || '');
      localStorage.setItem('accessToken', res.data.impersonationToken);
      localStorage.setItem('tenantId', String(id));
      localStorage.setItem('impersonationActive', 'true');
      localStorage.setItem('impersonatedTenantName', data.tenant.businessName);
      navigate('/admin');
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to impersonate tenant');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const formatCurrency = (v) => v ? `€${Number(v).toFixed(2)}` : '€0.00';

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  if (!data) return null;

  const tenant = data.tenant;
  const invoices = data.invoices || [];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/superadmin/tenants')} className="text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{tenant.businessName}</h1>
          <p className="text-sm text-gray-500">{tenant.subdomain} · Created {formatDate(tenant.createdAt)}</p>
        </div>
        <span className={`ml-auto inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[tenant.subscriptionStatus] || 'bg-gray-100 text-gray-700'}`}>
          {tenant.subscriptionStatus}
        </span>
      </div>

      {/* Message */}
      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationCircleIcon className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenant Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Tenant Settings</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <input
                type="email"
                value={form.contactEmail || ''}
                onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
              <select
                value={form.subscriptionStatus || ''}
                onChange={e => setForm(f => ({ ...f, subscriptionStatus: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="past_due">Past Due</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                value={form.planId || ''}
                onChange={e => setForm(f => ({ ...f, planId: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">— Select plan —</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>{p.name} (€{p.priceMonthly}/mo)</option>
                ))}
              </select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={!!form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded" />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={!!form.isSuspended} onChange={e => setForm(f => ({ ...f, isSuspended: e.target.checked }))} className="rounded" />
                Suspended
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleImpersonate}
                className="flex items-center gap-2 border border-purple-200 text-purple-700 hover:bg-purple-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Impersonate
              </button>
            </div>
          </div>
        </div>

        {/* Send Payment Link */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCardIcon className="w-5 h-5 text-purple-500" />
            Send Payment Link
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan to charge for</label>
              <select
                value={selectedPlanId}
                onChange={e => setSelectedPlanId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">— Select plan —</option>
                {plans.filter(p => p.priceMonthly > 0 || p.priceAnnual > 0).map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} — €{billingCycle === 'annual' ? p.priceAnnual : p.priceMonthly}/{billingCycle === 'annual' ? 'yr' : 'mo'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
              <div className="flex gap-2">
                {['monthly', 'annual'].map(cycle => (
                  <button
                    key={cycle}
                    onClick={() => setBillingCycle(cycle)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${billingCycle === cycle ? 'bg-purple-600 text-white' : 'border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {cycle}
                  </button>
                ))}
              </div>
            </div>
            {selectedPlanId && plans.length > 0 && (
              <div className="bg-purple-50 rounded-lg p-3 text-sm text-purple-800">
                <strong>Amount:</strong> €{
                  (() => {
                    const p = plans.find(x => String(x.id) === String(selectedPlanId));
                    return p ? (billingCycle === 'annual' ? p.priceAnnual : p.priceMonthly) : '0.00';
                  })()
                } {billingCycle}
              </div>
            )}
            <button
              onClick={handleSendPaymentLink}
              disabled={sendingLink || !selectedPlanId}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {sendingLink ? 'Creating link...' : 'Create Mollie Payment Link'}
            </button>
            <p className="text-xs text-gray-400">
              A Mollie payment page will open. Copy the link and send it to the tenant, or it will open automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Usage</h2>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Staff', value: tenant.currentStaffCount },
            { label: 'Locations', value: tenant.currentLocationCount },
            { label: 'Bookings this month', value: tenant.bookingsThisMonth }
          ].map(stat => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stat.value ?? 0}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Invoices */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Invoice History</h2>
        </div>
        {invoices.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-400 text-sm">No invoices yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Invoice #</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Amount</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Status</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500 hidden sm:table-cell">Period</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500 hidden md:table-cell">Paid At</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Link</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{inv.invoiceNumber}</td>
                    <td className="px-4 py-2">{formatCurrency(inv.totalAmount)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${inv.status === 'paid' ? 'bg-green-100 text-green-800' : inv.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs hidden sm:table-cell">
                      {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs hidden md:table-cell">{formatDate(inv.paidAt)}</td>
                    <td className="px-4 py-2">
                      {inv.mollieCheckoutUrl && inv.status === 'open' && (
                        <a href={inv.mollieCheckoutUrl} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline text-xs">
                          Pay link
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
